import { Add, ArrowDownward, ArrowUpward, Delete, Done, Edit } from '@mui/icons-material';
import { Alert, Box, Button, Chip, IconButton, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { ILevel } from '@sharedInterfaces/ICompanySettings';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import editLevelDefinitions from '@src/APIs/graphQl/Company/editLevelDefinitions';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Company from '@src/Objects/Company';
import { setLevelDefinition } from '@store/reducer/levelDefinitionReducer';
import type { AppState } from '@store/store';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CardBox from '../../Cards/CardBox/CardBox';

interface EditLevelListProps {
    level: ILevel[];
    readOnly?: true;
    setCompany: (val: Company) => void;
}

function EditLevelList(props: EditLevelListProps) {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).settings;
    const dispatch = useDispatch();

    const [saving, setSaving] = React.useState(false);
    const [changed, setChanged] = React.useState(false);
    const [editLevel, setEditLevel] = React.useState<null | ILevel[]>(null);
    const [saveError, setSaveError] = React.useState<string | null>(null);

    const getLevelHeaderColor = (level: number) => {
        const percentage = Math.min(level * 0.25, 1);
        return alpha(theme.palette.primary.main, percentage);
    };

    const getContrastText = (level: number) => {
        // Level 3, 4 und 5 bekommen weißen Text, Level 1 und 2 dunklen Text
        return level >= 3 ? theme.palette.common.white : theme.palette.text.primary;
    };

    const getLevelBadgeColor = (level: number) => {
        return theme.palette.levels?.[level as 1 | 2 | 3 | 4 | 5] || theme.palette.primary.main;
    };

    const handleSave = async () => {
        if (saving || !editLevel) return;

        if (!changed) {
            setEditLevel(null);
            return;
        }

        setSaving(true);
        setSaveError(null);

        try {
            const result = await editLevelDefinitions(editLevel);
            props.setCompany(result);
            setEditLevel(null);
            setChanged(false);
            dispatch(setLevelDefinition(result.level));
        } catch (ex) {
            setSaveError(String(ex));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
            <CardBox
                title={langStrings.levelDefinitions}
                rightButtons={
                    !props.readOnly && (
                        <IconButton
                            onClick={editLevel ? handleSave : () => setEditLevel(props.level.slice())}
                            disabled={saving}
                            color="primary"
                        >
                            {editLevel ? <Done /> : <Edit />}
                        </IconButton>
                    )
                }
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {saveError && (
                        <Alert severity="error" onClose={() => setSaveError(null)}>
                            {saveError}
                        </Alert>
                    )}

                    {(editLevel ? editLevel : props.level).map((level, index) => {
                        const headerColor = getLevelHeaderColor(level.level);
                        const contrastText = getContrastText(level.level);

                        return (
                            <CardBox
                                key={level.level}
                                style={{
                                    background: `linear-gradient(135deg, ${alpha(headerColor, 0.05)} 0%, transparent 100%)`,
                                }}
                            >
                                <Box
                                    sx={{
                                        bgcolor: headerColor,
                                        color: contrastText,
                                        p: 2,
                                        mx: -3,
                                        mt: -3,
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    {editLevel ? (
                                        <TextField
                                            value={level.title}
                                            onChange={(e) => {
                                                const newEditLevel = editLevel.slice().map((l) => ({ ...l }));
                                                newEditLevel[index].title = e.target.value;
                                                setEditLevel(newEditLevel);
                                                setChanged(true);
                                            }}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                flexGrow: 1,
                                                mr: 2,
                                                '& .MuiOutlinedInput-root': {
                                                    color: contrastText,
                                                    '& fieldset': { borderColor: alpha(contrastText, 0.5) },
                                                    '&:hover fieldset': { borderColor: contrastText },
                                                    '&.Mui-focused fieldset': { borderColor: contrastText },
                                                },
                                                '& .MuiInputLabel-root': { color: alpha(contrastText, 0.7) },
                                            }}
                                        />
                                    ) : (
                                        <Typography
                                            variant="h6"
                                            sx={{ fontWeight: 600, flexGrow: 1, color: contrastText }}
                                        >
                                            {level.title}
                                        </Typography>
                                    )}

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                            icon={getAppIcon(EIconCategory.LEVEL)}
                                            label={
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {`${langStrings.level} ${level.level}`}
                                                </Typography>
                                            }
                                            sx={{
                                                bgcolor: getLevelBadgeColor(level.level),
                                                color: theme.palette.getContrastText(getLevelBadgeColor(level.level)),
                                                '& .MuiChip-icon': {
                                                    color: 'inherit',
                                                },
                                            }}
                                        />

                                        {editLevel && (
                                            <>
                                                {level.level > 1 && (
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: contrastText }}
                                                        onClick={() => {
                                                            const newEditLevel = editLevel.slice();
                                                            newEditLevel[index - 1].level++;
                                                            newEditLevel[index].level--;
                                                            newEditLevel.sort((a, b) => a.level - b.level);
                                                            setEditLevel(newEditLevel);
                                                            setChanged(true);
                                                        }}
                                                    >
                                                        <ArrowUpward />
                                                    </IconButton>
                                                )}
                                                {level.level < editLevel.length && (
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: contrastText }}
                                                        onClick={() => {
                                                            const newEditLevel = editLevel.slice();
                                                            newEditLevel[index].level++;
                                                            newEditLevel[index + 1].level--;
                                                            newEditLevel.sort((a, b) => a.level - b.level);
                                                            setEditLevel(newEditLevel);
                                                            setChanged(true);
                                                        }}
                                                    >
                                                        <ArrowDownward />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: theme.palette.error.main }}
                                                    onClick={() => {
                                                        const newEditLevel = editLevel.slice();
                                                        newEditLevel.splice(index, 1);
                                                        setEditLevel(newEditLevel);
                                                        setChanged(true);
                                                    }}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </>
                                        )}
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {editLevel ? (
                                        <>
                                            <TextField
                                                label={langStrings.description}
                                                value={level.description}
                                                onChange={(e) => {
                                                    const newEditLevel = editLevel.slice();
                                                    newEditLevel[index].description = e.target.value;
                                                    setEditLevel(newEditLevel);
                                                    setChanged(true);
                                                }}
                                                multiline
                                                rows={3}
                                                variant="outlined"
                                                fullWidth
                                            />
                                            <TextField
                                                label={langStrings.explanation}
                                                value={level.helperText}
                                                onChange={(e) => {
                                                    const newEditLevel = editLevel.slice();
                                                    newEditLevel[index].helperText = e.target.value;
                                                    setEditLevel(newEditLevel);
                                                    setChanged(true);
                                                }}
                                                multiline
                                                rows={3}
                                                variant="outlined"
                                                fullWidth
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {langStrings.description}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                                                >
                                                    {level.description}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {langStrings.explanation}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
                                                >
                                                    {level.helperText}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </CardBox>
                        );
                    })}

                    {editLevel && (
                        <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => {
                                const newEditLevel = editLevel.slice();
                                newEditLevel.push({
                                    level: newEditLevel.length + 1,
                                    title: langStrings.newLevelDefinition,
                                    description: '',
                                    helperText: '',
                                });
                                setEditLevel(newEditLevel);
                                setChanged(true);
                            }}
                            disabled={saving}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            {langStrings.newLevelDefinition}
                        </Button>
                    )}
                </Box>
            </CardBox>
        </Box>
    );
}

export default EditLevelList;
